import {
  Business as OfficeIcon,
  Computer as DeskIcon
} from '@mui/icons-material'
import { Card, CardContent, Typography, Box, Chip } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import db from '../../db/db'
import { OfficeWithRole } from '../../utils/profileHelpers'
import { RoleDisplay, RoleBasedActions } from '../common'

/**
 * Props for the OfficeManagementCard component
 */
export interface OfficeManagementCardProps {
  /** Office data with role information */
  office: OfficeWithRole
}

/**
 * OfficeManagementCard - Individual office management card component
 *
 * Displays individual office information with management options.
 * Shows office name, role, desk count, and action buttons.
 * Provides view, edit, and desk management capabilities based on user role.
 */
const OfficeManagementCard = ({ office }: OfficeManagementCardProps) => {
  const navigate = useNavigate()
  const [deskCount, setDeskCount] = useState<number>(0)
  const [loadingDesks, setLoadingDesks] = useState(true)

  // Fetch desk count for this office
  useEffect(() => {
    const fetchDeskCount = async () => {
      if (!office.oid) return

      try {
        setLoadingDesks(true)
        const desks = await db.query({
          collection: 'desks',
          where: ['office', '==', office.oid]
        })
        setDeskCount(desks.length)
      } catch (error) {
        console.error('Error fetching desk count:', error)
        setDeskCount(0)
      } finally {
        setLoadingDesks(false)
      }
    }

    fetchDeskCount()
  }, [office.oid])

  const handleViewOffice = () => {
    if (office.oid) {
      navigate(`/office/${office.oid}`)
    }
  }

  const handleEditOffice = () => {
    if (office.oid) {
      navigate(`/edit-office/${office.oid}`)
    }
  }

  const handleAddDesk = () => {
    if (office.oid) {
      navigate(`/create-desk/${office.oid}`)
    }
  }

  return (
    <Card
      variant='outlined'
      sx={{
        mb: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 2,
          transform: 'translateY(-1px)'
        }
      }}
    >
      <CardContent>
        <Box>
          {/* Office Header */}
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            flexWrap='wrap'
            gap={2}
            mb={2}
          >
            {/* Office name with icon */}
            <Box
              display='flex'
              alignItems='center'
              gap={1}
              flex={1}
              minWidth={0}
            >
              <OfficeIcon color='primary' fontSize='medium' />
              <Typography
                variant='h6'
                component='h3'
                sx={{
                  fontWeight: 'medium',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {office.name || 'Unnamed Office'}
              </Typography>
            </Box>

            {/* Role display */}
            <Box flexShrink={0}>
              <RoleDisplay role={office.userRole} variant='chip' size='small' />
            </Box>
          </Box>

          {/* Office Info */}
          <Box display='flex' alignItems='center' gap={2} mb={3}>
            <Box display='flex' alignItems='center' gap={1}>
              <DeskIcon fontSize='small' color='action' />
              <Typography variant='body2' color='text.secondary'>
                {loadingDesks
                  ? 'Loading...'
                  : `${deskCount} desk${deskCount !== 1 ? 's' : ''}`}
              </Typography>
            </Box>

            {office.description && (
              <Chip
                label='Has Description'
                size='small'
                variant='outlined'
                sx={{ fontSize: '0.75rem' }}
              />
            )}
          </Box>

          {/* Action Buttons */}
          <RoleBasedActions
            office={office}
            onView={handleViewOffice}
            onEdit={handleEditOffice}
            onAddDesk={handleAddDesk}
            size='small'
            variant='spaced'
          />
        </Box>
      </CardContent>
    </Card>
  )
}

export default OfficeManagementCard
